import { Pencil, Reply } from 'lucide-react'
import { Fragment, useState } from 'react'

import { CallActivityCard } from '@/components/Aircall/CallActivityCard'
import { leadContactNumbers } from '@/components/Aircall/contactNumbers'
import { RichTextareaWithSubmit } from '@/components/composer/RichTextareaWithSubmit'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { type InsuranceCarrier } from '@/models/insuranceCarrier'
import {
  type SalesLead,
  type SalesLeadActivity,
  SalesLeadActivityType,
  useUpdateActivity,
} from '@/models/salesLead'
import { ActivityCard } from './ActivityCard'
import { CarrierEmailComposer } from './CarrierEmailComposer'
import { leadContactEmails } from './contactEmails'
import { DraftActivityCard } from './DraftActivityCard'
import { SendEmailForm } from './SendEmailForm'
import { SmsDraftActivityCard } from './SmsDraftActivityCard'
import { SmsReplyForm } from './SmsReplyForm'

const CALL_ACTIVITY_TYPES = new Set<SalesLeadActivityType>([
  SalesLeadActivityType.CALL_INBOUND,
  SalesLeadActivityType.CALL_OUTBOUND,
  SalesLeadActivityType.CALL_INBOUND_MISSED,
  SalesLeadActivityType.CALL_OUTBOUND_MISSED,
])

interface ActivityItemProps {
  lead: SalesLead
  activity: SalesLeadActivity
  // Present when rendered inside a carrier card — replies thread as carrier
  // emails to the carrier contact rather than lead emails to the customer.
  carrier?: InsuranceCarrier
  isReplying: boolean
  onReply: (activityId: string) => void
  onCancelReply: () => void
}

export function ActivityItem({
  lead,
  activity,
  carrier,
  isReplying,
  onReply,
  onCancelReply,
}: ActivityItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(activity.notes)
  const updateActivity = useUpdateActivity()

  if (activity.activityType === SalesLeadActivityType.EMAIL_OUTBOUND_DRAFT) {
    return (
      <DraftActivityCard leadId={lead.id} activity={activity} emails={leadContactEmails(lead.contacts)} />
    )
  }

  if (activity.activityType === SalesLeadActivityType.SMS_OUTBOUND_DRAFT) {
    return (
      <SmsDraftActivityCard
        leadId={lead.id}
        activity={activity}
        numbers={leadContactNumbers(lead.contacts)}
      />
    )
  }

  if (CALL_ACTIVITY_TYPES.has(activity.activityType)) {
    return <CallActivityCard leadId={lead.id} activity={activity} />
  }

  const isNote = activity.activityType === SalesLeadActivityType.NOTE
  const isInboundEmail = activity.activityType === SalesLeadActivityType.EMAIL_INBOUND
  const isInboundSms = activity.activityType === SalesLeadActivityType.SMS_INBOUND
  const replySubject = activity.emailSubject
    ? /^re:/i.test(activity.emailSubject)
      ? activity.emailSubject
      : `Re: ${activity.emailSubject}`
    : 'Re:'
  // Without the carrier record the composer would draft to the insured instead.
  const canReply = !activity.insuranceCarrier || !!carrier
  const isAiSummary = activity.activityType === SalesLeadActivityType.AI_SUMMARY
  const isUnread =
    !isAiSummary && !!lead.lastReadAt && !activity.createdByMe && activity.createdAt > lead.lastReadAt

  const startEdit = () => {
    setDraft(activity.notes)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setDraft(activity.notes)
  }

  const saveEdit = () => {
    updateActivity.mutate(
      { leadId: lead.id, activityId: activity.id, notes: draft },
      { onSuccess: () => setIsEditing(false) }
    )
  }

  const trimmedDraft = draft.trim()
  const saveDisabled = updateActivity.isPending || trimmedDraft.length === 0 || draft === activity.notes

  if (isNote && isEditing) {
    return (
      <RichTextareaWithSubmit
        editorKey={activity.id}
        value={draft}
        onValueChange={setDraft}
        buttonProps={{
          disabled: saveDisabled,
          onClick: saveEdit,
          'aria-label': 'Save',
        }}
        addonChildren={
          <>
            <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={updateActivity.isPending}>
              Cancel
            </Button>
            {updateActivity.isError && <span className="text-destructive text-sm">Failed to save</span>}
          </>
        }
      />
    )
  }

  return (
    <Fragment>
      {isReplying &&
        (carrier ? (
          <CarrierEmailComposer
            lead={lead}
            carrier={carrier}
            initialContactId={activity.insuranceCarrierContact ?? ''}
            inReplyToActivityId={activity.id}
            initialSubject={replySubject}
            onSent={onCancelReply}
            onCancel={onCancelReply}
          />
        ) : isInboundSms ? (
          <SmsReplyForm
            leadId={lead.id}
            inReplyToActivityId={activity.id}
            to={activity.smsFrom ?? null}
            onSent={onCancelReply}
            onCancel={onCancelReply}
          />
        ) : (
          <SendEmailForm
            lead={lead}
            templates={[]}
            defaultTemplateId={null}
            signature=""
            initialSubject={replySubject}
            initialBody=""
            inReplyToActivityId={activity.id}
            hideSubject
            resetKey={activity.id}
            onSent={onCancelReply}
            onCancel={onCancelReply}
          />
        ))}
      <ActivityCard
        activity={activity}
        className={cn(
          isUnread && 'border-blue-300 bg-blue-50/50',
          isAiSummary && 'border-yellow-300 bg-yellow-50/60'
        )}
        corner={
          isNote &&
          !isEditing && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground absolute top-2 right-2 size-6 hover:bg-transparent"
              onClick={startEdit}
              aria-label="Edit note"
            >
              <Pencil className="size-3" />
            </Button>
          )
        }
        headerEnd={
          (isInboundEmail || isInboundSms) &&
          !isReplying &&
          canReply && (
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => onReply(activity.id)}>
              <Reply className="mr-1 size-4" />
              Reply
            </Button>
          )
        }
      />
    </Fragment>
  )
}
